import { ProvisionType } from '@wix/ambassador-funnel-intro-v1-meta-site-provision-descriptor/types'
import { Box, Button, Input, InputImperativeActions, Loader, Tag } from '@wix/design-system'
import { useContent, useExperiments, useMetadata, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { retry, tryCatch } from '@wix/funnel-utils'
import React, { FC, useEffect, useRef, useState } from 'react'

import { provisionMetasite } from '../../../api/FunnelIntroSiteProvisioningService/provisionMetasite'
import { suggestDomains } from '../../../api/suggestDomains'
import { useDebouncedTypingLogger, usePageLogger } from '../../../bi/mfi'
import { resolveCategoryFromBusinessTerm } from '../../../category'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { WixMobileBackButton } from '../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileContentWrapper } from '../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileDescription } from '../../reusable/WixMobileDescription/WixMobileDescription'
import { WixMobileFooter } from '../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileSkipButton } from '../../reusable/WixMobileSkipButton/WixMobileSkipButton'
import { WixMobileStepProgressBar } from '../../reusable/WixMobileStepProgressBar/WixMobileStepProgressBar'
import { TWixMobileBusinessName as Content } from './WixMobileBusinessName.content'
import { wixMobileBusinessNameDataHooks as dataHooks } from './WixMobileBusinessName.dataHooks'

export const WixMobileBusinessName: FC = () => {
  const { content } = useContent<Content>()
  const { questionKey } = content.question
  const { save, appDefIds = [], businessName: previousBusinessName, category, nameSuggestions } = useStorageContext()
  const [businessName, setBusinessName] = useState(previousBusinessName || '')
  const answerTypeRef = useRef<'chips' | 'free text'>('free text')
  const [pageLoading] = usePageLoading()
  const experiments = useExperiments()
  const pageLogger = usePageLogger()
  const debouncedTyping = useDebouncedTypingLogger(pageLogger)
  const next = useNext()
  const [isSaveSiteDetailsInProgress, setIsSaveSiteDetailsInProgress] = useState(false)
  const [{ biSlug: flowBiSlug }] = useMetadata()
  const inputRef = useRef<InputImperativeActions<HTMLInputElement>>()

  const isBetterBusinessName = experiments['specs.funnel.BetterMobileBusinessName'] === 'true'

  const provisionMetaSite = async () => {
    const { industryId, structureId, displayName, postRegVertical, businessTermId, businessSegmentId } =
      resolveCategoryFromBusinessTerm(category?.businessTerm, category?.businessSegmentId)

    const response = await provisionMetasite({
      type: ProvisionType.DASHBOARD_FIRST,
      origin: flowBiSlug,
      metadata: {
        flow: flowBiSlug,
      },
      additionalIntroData: {
        businessName,
        industryId,
        structureId,
        category: {
          industryId,
          structureId,
          displayName,
          postRegVertical,
          businessTermId,
          businessSegmentId,
        },
        selectedApps: appDefIds.map(appDefIds => ({ appDefId: appDefIds })),
      },
    })

    const metaSiteId = response.data.metaSiteProvisionDescriptor?.metaSiteId || undefined

    save({ provisionedMetaSiteId: metaSiteId })

    return metaSiteId
  }

  const suggestDomain = async () => {
    if (!businessName) {
      return
    }

    const response = await suggestDomains({ query: businessName, limit: 1 })
    const suggestion = response.data.suggestions?.[0]
    save({ suggestedDomain: suggestion?.domain || '' })
  }

  const onBackButtonClick = () => {
    void pageLogger.generalAction({ action: 'back' })
    window.history.back()
  }

  const onSkipButtonClick = async () => {
    if (pageLoading || isSaveSiteDetailsInProgress) {
      return
    }
    setIsSaveSiteDetailsInProgress(true)

    const [error, metaSiteId] = await tryCatch(() => retry(() => provisionMetaSite()))

    if (!error) {
      void pageLogger.skip()
      next({ [questionKey]: { skipped: true } }, { metaSiteId })
    }

    setIsSaveSiteDetailsInProgress(false)
  }

  const onNextButtonClick = async () => {
    if (pageLoading || isSaveSiteDetailsInProgress) {
      return
    }

    setIsSaveSiteDetailsInProgress(true)

    // won't do anyting if clicked on next button, but will blur if enter is pressed
    inputRef?.current?.blur()
    save({ businessName })

    const provisionMetasitePromise = tryCatch(() => retry(() => provisionMetaSite()))
    const suggestedDomainPromise = tryCatch(() => retry(() => suggestDomain()))
    const [[error, metaSiteId]] = await Promise.all([provisionMetasitePromise, suggestedDomainPromise])

    if (!error) {
      if (!businessName) {
        await pageLogger.skip()
        next({ [questionKey]: { skipped: true } }, { metaSiteId })
        return
      }

      void pageLogger.answer({
        answer: businessName,
        answerType: answerTypeRef.current,
        freeText: businessName,
      })
      next({ [questionKey]: { values: [businessName], skipped: !businessName } }, { metaSiteId })
      return
    }

    setIsSaveSiteDetailsInProgress(false)
  }

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  useEffect(() => {
    if (isBetterBusinessName) {
      inputRef.current?.focus()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const suggestionsRef = useRef<HTMLDivElement>()
  // What this effect does - prevents losing focus on input on click on suggestion
  useEffect(() => {
    const el = suggestionsRef.current
    if (!el || !isBetterBusinessName) {
      return
    }
    const options = { capture: true }
    const handler = (e: PointerEvent) => {
      if (e.target instanceof HTMLSpanElement) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
    el.addEventListener('pointerdown', handler, options)
    return () => {
      el.removeEventListener('pointerdown', handler, options)
    }
  }, [isBetterBusinessName])

  const handleSuggestionClick = (sugg: string, index: number) => {
    answerTypeRef.current = 'chips'
    void pageLogger.clickAnswer({ questionKey, buttonName: sugg, suggestedOption: true, position: index + 1 })
    setBusinessName(sugg)
  }

  useEffect(() => {
    if (!isBetterBusinessName) {
      return
    }
    inputRef.current?.input?.setAttribute('enterkeyhint', 'next')
  }, [isBetterBusinessName])

  return (
    <WixMobilePage>
      <WixMobileHeader
        leftContent={<WixMobileBackButton onBack={onBackButtonClick} dataHook={dataHooks.back()} />}
        rightContent={
          (content.skip && (
            <WixMobileSkipButton dataHook={dataHooks.skip()} onSkip={onSkipButtonClick} label={content.skip} />
          )) ||
          undefined
        }
      />
      <WixMobileContentWrapper>
        <WixMobileStepProgressBar
          current={content.stepProgressBar.content.current}
          total={content.stepProgressBar.content.total}
          dataHook={dataHooks.stepProgressBar()}
        />
        <WixMobileHeading dataHook={dataHooks.title()}>{content.question.content.title}</WixMobileHeading>
        <WixMobileDescription dataHook={dataHooks.subtitle()}>
          <span id="description">{content.question.content.subtitle}</span>
        </WixMobileDescription>
        <Input
          ref={inputRef as any}
          value={businessName}
          onChange={e => {
            answerTypeRef.current = 'free text'
            setBusinessName(e.target.value)
            debouncedTyping(e.target.value)
          }}
          placeholder={content.question.content.placeholder}
          dataHook={dataHooks.input()}
          clearButton
          onClear={() => {
            answerTypeRef.current = 'free text'
            setBusinessName('')
          }}
          size="large"
          onEnterPressed={isBetterBusinessName ? onNextButtonClick : undefined}
        />
        {!!nameSuggestions?.length && isBetterBusinessName && (
          <Box
            direction="horizontal"
            gap="12px"
            flexWrap="wrap"
            align="center"
            padding="20px 20px 0"
            ref={suggestionsRef}
          >
            {nameSuggestions.map((suggestion, index) => (
              <Tag
                key={suggestion}
                id={suggestion}
                onClick={() => handleSuggestionClick(suggestion, index)}
                size="medium"
                removable={false}
              >
                {suggestion}
              </Tag>
            ))}
          </Box>
        )}
      </WixMobileContentWrapper>
      <WixMobileFooter>
        {content.primaryBtn && (
          <Button size="large" fullWidth onClick={onNextButtonClick} dataHook={dataHooks.primaryBtn()}>
            <Box gap="SP1" verticalAlign="middle">
              {isSaveSiteDetailsInProgress ? <Loader size="tiny" /> : content.primaryBtn}
            </Box>
          </Button>
        )}
      </WixMobileFooter>
    </WixMobilePage>
  )
}
